/**
 * User Slack Identity
 * Component to link AI Studio user to a Slack user
 */

import {
  Stack,
  Title,
  Select,
  useToast,
  CircularLoader,
  useActions
} from "@introist/react-foundation/v2";
import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi, channelsApi } from "../api";
import slackIcon from "../../assets/Icons/slack.svg";
import { Skeletons } from "../../components/molecules";

interface UserSlackIdentityProps {
  userId: string;
}

interface SlackUser {
  id: string;
  name: string;
  real_name?: string;
}

export const UserSlackIdentity = ({ userId }: UserSlackIdentityProps) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { onConfirmAction } = useActions();

  // Fetch all channels to find a Slack channel
  const { data: channels = [] } = useQuery({
    queryKey: ["ai-studio", "channels"],
    queryFn: () => channelsApi.list()
  });

  // Find the first Slack channel
  const slackChannel = useMemo(() => channels.find(c => c.platform === "slack"), [channels]);

  // Fetch user identities
  const { data: identities = [], isLoading: identitiesLoading } = useQuery({
    queryKey: ["ai-studio", "users", userId, "identities"],
    queryFn: () => usersApi.listIdentities(userId)
  });

  // Fetch Slack users if we have a Slack channel
  const { data: slackUsers = [], isLoading: slackUsersLoading } = useQuery({
    queryKey: ["ai-studio", "channels", slackChannel?.id, "users"],
    queryFn: () => channelsApi.getChannelUsers(slackChannel!.id),
    enabled: !!slackChannel
  });

  // Find the slack identity if it exists
  const slackIdentity = useMemo(
    () => identities.find(i => i.external_system === "slack"),
    [identities]
  );

  // Link mutation
  const linkMutation = useMutation({
    mutationFn: async (slackUserId: string) => {
      const slackUser = slackUsers.find((u: SlackUser) => u.id === slackUserId);
      if (!slackUser) throw new Error("Slack user not found");

      return usersApi.createIdentity(userId, {
        external_system: "slack",
        external_id: slackUserId,
        external_name: slackUser.real_name || slackUser.name
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-studio", "users", userId, "identities"]
      });
      toast.success("Linked to Slack user");
    },
    onError: (error: any) => {
      toast.error(`Failed to link Slack user: ${error.message}`);
    }
  });

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      if (!slackIdentity) throw new Error("No identity to unlink");
      return usersApi.deleteIdentity(userId, slackIdentity.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-studio", "users", userId, "identities"]
      });
      toast.success("Unlinked from Slack user");
    },
    onError: (error: any) => {
      toast.error(`Failed to unlink Slack user: ${error.message}`);
    }
  });

  const handleSelect = async (option: { key: string }) => {
    await linkMutation.mutateAsync(option.key);
  };

  const handleUnlink = onConfirmAction(
    async () => {
      await unlinkMutation.mutateAsync();
    },
    {
      title: "Unlink Slack user",
      description: "Are you sure you want to unlink this user from the Slack user?",
      confirmTitle: "Unlink"
    }
  );

  const slackUserOptions = useMemo(() => {
    return slackUsers.map((user: SlackUser) => ({
      key: user.id,
      title: user.real_name || user.name
    }));
  }, [slackUsers]);

  const selectedValue = slackIdentity?.external_id || "";

  // Don't render if no Slack channel is configured
  if (!slackChannel) {
    return null;
  }

  if (slackUsersLoading || identitiesLoading) {
    return <Skeletons.Block height={50} />;
  }

  return (
    <Stack style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
      <Stack style={{ alignItems: "center", gap: "var(--spacing-small)" }}>
        <img src={slackIcon} alt="Slack" width={20} />
        <Title variant="bold">Slack</Title>
      </Stack>

      <Select
        size="small"
        variant="outlined"
        value={selectedValue}
        onSelect={handleSelect}
        options={slackUserOptions}
        endAdornment={
          linkMutation.isLoading || unlinkMutation.isLoading ? (
            <CircularLoader size="tiny" />
          ) : undefined
        }
        placeholder="Link to Slack user"
        disabled={identitiesLoading || slackUsersLoading}
        style={{ width: "100%" }}
        searchable
        onClear={handleUnlink}
      />
    </Stack>
  );
};
